import * as React from 'react';
import styled from 'styled-components';
import isToday from '@sdk/utils/dateUtils/isToday';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';

import type { RowDataType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
} from '../types';
import type { FormattedDateString } from '../types/FormattedDateString';
import FlexRow from '../ui/FlexRow';
import EventBox from '../EventBox';
import ShowPlus from '../components/ShowPlus';
import { IsEventSelectedType, ToggleEventType } from '../hooks/useEventSelection';
import useHoverColumns from '../hooks/useHoverColumns';

import ScheduleCell from './ScheduleCell';

const Row = styled(FlexRow)`
  height: 100%;
  background-color: #f3f5f7;
`;

type Props = {
  columns: Array<FormattedDateString>;
  rowData: RowDataType;
  openEvent: OpenEventCallback;
  columnsCount?: number;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  shouldShowPlus: ShouldShowPlusType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
};
const RegularRow: React.FC<React.PropsWithChildren<Props>> = ({
  columns,
  rowData,
  openEvent,
  columnsCount,
  shiftAppearanceMode,
  addEvent,
  toggleEvent,
  hasSelectedEvents,
  isEventSelected,
  isShiftLineVisible,
  shouldShowPlus,
  isBusinessClosed,
  isHolidayPay,
}) => {
  const {
    hasNameColumn = true,
    events,
    group,
    user,
    isLast,
    eventHeight,
    withBottomSeparator,
  } = rowData;
  const { isHovered, toggleHover } = useHoverColumns(columns);

  return (
    <Row>
      {hasNameColumn && (
        <ScheduleCell
          columnsCount={columnsCount}
          isLast={isLast}
          bottomBorder={withBottomSeparator}
        />
      )}
      {columns.map((day) => {
        const event = events[day];
        const userForEvent = (group as any).isForDtstart ? null : user;
        const toggle = (): void => toggleHover(day);

        const showPlus: boolean =
          isHovered(day) && shouldShowPlus({ user: userForEvent, group, day });

        return (
          <ScheduleCell
            key={day}
            columnsCount={columnsCount}
            isToday={isToday(day)}
            isBusinessClosed={isBusinessClosed({ day })}
            isHolidayPay={isHolidayPay({ day })}
            isLast={isLast}
            onMouseEnter={toggle}
            onMouseLeave={toggle}
            bottomBorder={withBottomSeparator}>
            {Boolean(event) && (
              <EventBox
                key={event.id}
                event={event}
                openEvent={openEvent}
                shiftAppearanceMode={shiftAppearanceMode}
                hoverComponent={ScheduleCell}
                toggleEvent={toggleEvent}
                hasSelectedEvents={hasSelectedEvents}
                isEventSelected={isEventSelected}
                isExchange={false}
                isShiftLineVisible={isShiftLineVisible}
                height={eventHeight}
                showPlus={showPlus}
                isHovered={isHovered(day)}
              />
            )}

            {showPlus && (
              <ShowPlus day={day} group={group} addEvent={addEvent} event={event} user={user} />
            )}
          </ScheduleCell>
        );
      })}
    </Row>
  );
};

export default RegularRow;
